import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useSelector, useDispatch } from 'react-redux';
import { useOrganizationTranslation } from '../hooks/useOrganizationTranslation';
import QText from './QText';
import UserModeIcon from './Icons/UserModeIcon';
import BusinessIcon from './Icons/BusinessIcon';
import UserIcon from './Icons/UserIcon';
import { setRole } from '../store/slice/roleSlice';
import { setRoleLocalStorage } from '../utils';

const ModeToggle = ({ onModeChange }) => {
  const { t } = useOrganizationTranslation();
  const theme = useTheme();
  const role = useSelector((state) => state.role);
  const dispatch = useDispatch();
  const styles = createStyles(theme);

  const toggleUserMode = async () => {
    const newRole = role === 'user' ? 'business' : 'user';
    dispatch(setRole(newRole));
    await setRoleLocalStorage(newRole);
    if (onModeChange) {
      onModeChange();
    }
  };

  return (
    <View style={styles.modeToggleContainer}>
      <View style={styles.modeHeader}>
        <UserModeIcon
          width={20}
          height={20}
          color={theme.colors.secondaryText}
        />
        <QText style={styles.modeHeaderText}>{t('mode')}</QText>
      </View>

      <View style={styles.toggleContainer}>
        <TouchableOpacity
          style={[
            styles.toggleButton,
            styles.leftButton,
            role === 'business' && styles.activeButton,
          ]}
          onPress={() => role !== 'business' && toggleUserMode()}
          activeOpacity={0.8}
        >
          <View style={styles.buttonIcon}>
            <BusinessIcon
              width={16}
              height={16}
              color={
                role === 'business'
                  ? theme.colors.whiteTxt
                  : theme.colors.placeHolderText
              }
            />
          </View>
          <QText
            style={[
              styles.toggleText,
              role === 'business' && styles.activeToggleText,
            ]}
          >
            {t('business')}
          </QText>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.toggleButton,
            styles.rightButton,
            role === 'user' && styles.activeButton,
          ]}
          onPress={() => role !== 'user' && toggleUserMode()}
          activeOpacity={0.8}
        >
          <View style={styles.buttonIcon}>
            <UserIcon
              width={16}
              height={16}
              color={
                role === 'user'
                  ? theme.colors.whiteTxt
                  : theme.colors.placeHolderText
              }
            />
          </View>
          <QText
            style={[
              styles.toggleText,
              role === 'user' && styles.activeToggleText,
            ]}
          >
            {t('user')}
          </QText>
        </TouchableOpacity>
      </View>

      <QText style={styles.infoText}>
        {role === 'business' ? t('business_mode_info') : t('user_mode_info')}
      </QText>
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    modeToggleContainer: {
      paddingHorizontal: 16,
      paddingVertical: 16,
    },
    modeHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    modeHeaderText: {
      fontSize: 14,
      fontWeight: '500',
      color: theme.colors.secondaryText,
      marginLeft: 12,
    },
    toggleContainer: {
      flexDirection: 'row',
      backgroundColor: theme.colors.background,
      borderRadius: 8,
      padding: 4,
      marginBottom: 12,
    },
    toggleButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      paddingHorizontal: 12,
      borderRadius: 6,
      minHeight: 48,
    },
    leftButton: {
      marginRight: 1,
    },
    rightButton: {
      marginLeft: 1,
    },
    activeButton: {
      backgroundColor: theme.colors.primary,
    },
    buttonIcon: {
      marginRight: 6,
    },
    toggleText: {
      fontSize: 13,
      fontWeight: '500',
      color: theme.colors.placeHolderText,
      textTransform: 'capitalize',
    },
    activeToggleText: {
      color: theme.colors.whiteTxt,
      fontWeight: '600',
    },
    infoText: {
      fontSize: 12,
      color: theme.colors.placeHolderText,
      textAlign: 'center',
      lineHeight: 16,
    },
  });

export default ModeToggle;
